// frontend-csv - CSV-serialisering til browseren. Rene funktioner, ingen DOM.
//
// Kilde: P_Kang_Youweis_Danmarksrejse/frontend/src/wei-csv.js, generaliseret.
// Kontrakt + profiltabel: P_webmoduler/python/CONTRACTS.md -> dashboard -> CSV-profiler.
// Python-siden (webmoduler.dashboard.to_csv) har de SAMME to profiler med de samme navne.
//
// To ting er bevidst UDE af modulet:
//   * download-delen (Blob + a[download] + revokeObjectURL) bor i forbrugeren. Den er
//     DOM- og UX-specifik, og uden den kan modulet koere under `node --test` og i en Worker.
//   * kolonne-listen. csvText tager den som argument, praecis som Python-sidens to_csv,
//     saa modulet ikke kender noget til det enkelte sites datamodel.

/**
 * De to navngivne profiler. Samme navne og samme betydning som paa Python-siden;
 * kun DEFAULTEN er forskellig, fordi forbrugerne er forskellige: her er det en
 * browser-download til et regneark, dér er det typisk en maskinlaesbar eksport.
 *
 *   sheet    semikolon + UTF-8-BOM. Dansk Excel deler paa semikolon (komma ville
 *            give een soejle) og gengiver kun aeoeaa + CJK korrekt naar BOM'en er der.
 *   machine  komma, ingen BOM. Byte-trofast til import/diff/pandas.
 *
 * Begge bruger CRLF som linjeskift (RFC 4180).
 */
export const PROFILER = Object.freeze({
  sheet:   Object.freeze({ sep: ';', bom: true }),
  machine: Object.freeze({ sep: ',', bom: false }),
});

// Tegn der faar Excel/LibreOffice/Google Sheets til at laese en celle som en FORMEL
// (CSV-/formel-injektion, OWASP). Samme saet som husets Python-writer
// (dashboard.py::_FORMULA_LEAD_CHARS), og samme kur: saet en apostrof foran, som er
// regnearkenes egen markoer for "dette er ren tekst".
const FORMELTEGN = new Set(['=', '+', '-', '@', '\t', '\r', '\n']);

/**
 * Render EEN celle.
 *
 * @param {*} v vaerdien; null/undefined bliver til "".
 * @param {{sep?: string, formulaGuard?: boolean}} [opts]
 *   sep           skilletegnet der skal quotes imod (default ";").
 *   formulaGuard  default true. Slaas kun fra naar forbrugeren IKKE er et regneark.
 * @returns {string}
 */
export function csvField(v, opts = {}) {
  const sep = opts.sep === undefined ? PROFILER.sheet.sep : opts.sep;
  const guard = opts.formulaGuard === undefined ? true : !!opts.formulaGuard;
  // Whitespace foldes til enkelte mellemrum, saa filen har praecis een linje pr. raekke.
  // Det er ogsaa saadan cellen ser ud paa skaermen (HTML folder selv whitespace), saa
  // eksporten og siden siger det samme.
  let s = String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
  if (guard && s && FORMELTEGN.has(s[0])) s = "'" + s;
  // Citat-tegn dobles, jf. RFC 4180. Quoting maales mod DEN AKTUELLE separator - ellers
  // ville et skift af profil tavst dele en vaerdi i to soejler.
  const skalQuotes = s.indexOf('"') >= 0 || s.indexOf('\n') >= 0 ||
                     (sep !== '' && s.indexOf(sep) >= 0);
  return skalQuotes ? '"' + s.replace(/"/g, '""') + '"' : s;
}

/**
 * Render hele dokumentet.
 *
 * @param {Array<Object>} rows raekker; hver vaerdi hentes med noeglen fra columns.
 * @param {Array<string>} columns kolonne-noegler OG header-tekst, i den raekkefoelge de skal staa.
 * @param {{profile?: string, formulaGuard?: boolean}} [opts]
 * @returns {string} CSV-teksten, med CRLF og (for sheet) en foranstillet BOM.
 * @throws {Error} hvis profilen er ukendt, eller columns er tom.
 */
export function csvText(rows, columns, opts = {}) {
  const navn = opts.profile === undefined ? 'sheet' : opts.profile;
  const p = PROFILER[navn];
  if (!p) {
    throw new Error(
      'csvText: ukendt profil ' + JSON.stringify(navn) +
      ' (kendte: ' + Object.keys(PROFILER).sort().join(', ') + ')'
    );
  }
  const cols = Array.from(columns || []);
  if (cols.length === 0) throw new Error('csvText: columns maa ikke vaere tom');
  const guard = opts.formulaGuard === undefined ? true : !!opts.formulaGuard;
  const felt = (v) => csvField(v, { sep: p.sep, formulaGuard: guard });

  const ud = [cols.map(felt).join(p.sep)];
  for (const r of rows || []) {
    ud.push(cols.map((c) => felt(r == null ? '' : r[c])).join(p.sep));
  }
  // BOM som escape, ikke som usynligt tegn i kilden: en literal U+FEFF ville vaere
  // umulig at se i en diff og let at slette ved en fejl.
  return (p.bom ? '\ufeff' : '') + ud.join('\r\n') + '\r\n';
}
